import type { ProductProfile } from "../../../shared/goals/profile";

/**
 * The profile walk (the prototype's `wk*`): the owner checks a drafted profile one part at a time, fixes
 * it in their own words and answers what only they know. The parts come from the draft itself; the
 * titles and hints below are the prototype's words for the sections the learn method usually writes.
 */

export const WK_T: Record<string, string> = {
  summary: "In one line",
  what: "What you sell",
  who: "Who it is for",
  offer: "Offer and price",
  next_step: "What you want people to do",
  voice: "How you sound",
  proof: "Proof",
  can_claim: "Safe to say",
  cannot_claim: "Never say",
};

export const WK_HINT: Record<string, string> = {
  what: "What you offer, in plain words.",
  who: "The customers you want more of.",
  offer: "What you charge, and any offer people can take up.",
  next_step:
    "The one action every ad and email should lead to. For example: book a call, get a home value, order online.",
  voice: "How your words sound, and who signs your messages.",
  proof: "Reviews, numbers and results we may show.",
  claims: "Claims that are safe to make, and the ones to avoid.",
};

const LEAD = ["what", "who", "offer", "next_step", "voice", "proof"];
const CLAIMS = ["can_claim", "cannot_claim"];

// Why: a question the agent did not tie to a section still belongs where its words point.
const WK_Q: [string, RegExp][] = [
  ["website", /website|domain|\burl\b|\bsite\b/i],
  ["next_step", /next step|conversion|call to action|drive|primary goal|main goal/i],
  ["voice", /\bsign|voice|tone|appear in ads|in what name|feature .* only/i],
  ["proof", /headline number|numbers to use|figure|statistic|review|proof|rating|years|count/i],
  ["offer", /offer|discount|price|promot|guarantee|welcome kit/i],
  ["who", /lead with|more of|audience|buyer|customer|area|serve|segment|product should/i],
  ["claims", /never say|not say|avoid|claim|must we never|will not/i],
];

export type PartKind = "site" | "sections" | "found" | "more";

export interface Part {
  id: string;
  title: string;
  kind: PartKind;
  secs: string[];
  /** Indexes into profile.questions. */
  qs: number[];
}

export function walkParts(p: ProductProfile): Part[] {
  const ids = new Set(p.sections.filter((s) => s.id !== WORDS).map((s) => s.id));
  const parts: Part[] = [{ id: "website", title: "Your business", kind: "site", secs: [], qs: [] }];
  for (const id of LEAD) if (ids.has(id)) parts.push({ id, title: WK_T[id], kind: "sections", secs: [id], qs: [] });
  // Why: the method may write sections beyond the usual ones; each gets a part of its own, under its own title.
  for (const s of p.sections)
    if (!LEAD.includes(s.id) && !CLAIMS.includes(s.id) && s.id !== WORDS) parts.push({ id: s.id, title: s.title, kind: "sections", secs: [s.id], qs: [] });
  const cl = CLAIMS.filter((id) => ids.has(id));
  if (cl.length) parts.push({ id: "claims", title: "What we may say", kind: "sections", secs: cl, qs: [] });
  if (p.problems.length || allRules(p).length || p.calls_to_action.length || p.presence.length || p.market.length)
    parts.push({ id: "found", title: "Also worth knowing", kind: "found", secs: [], qs: [] });
  parts.push({ id: "more", title: "Anything we missed?", kind: "more", secs: [], qs: [] });
  p.questions.forEach((q, k) => {
    let a = q.about && q.about !== "other" ? q.about : (WK_Q.find(([, r]) => r.test(q.question))?.[0] ?? "more");
    if (CLAIMS.includes(a)) a = "claims";
    const part = parts.find((x) => x.id === a) ?? parts[parts.length - 1];
    part.qs.push(k);
  });
  return parts;
}

export interface WalkState {
  at: number;
  /** The owner's words for a section (by id) or for "summary". */
  ed: Record<string, string>;
  site: { name?: string; website?: string };
  note: Record<string, string>;
  noteOpen: string | null;
  ok: Record<string, boolean>;
  /** An option's index, or the owner's own words. */
  ans: Record<number, number | string>;
  pb: Record<number, "noted" | "wrong">;
  pbw: Record<number, string>;
  /** Rules for the kind of business the owner said are not right, with what is right. */
  wo: Record<number, string>;
  extra: string;
  /** What a saved profile held when it was opened, so only real changes count. */
  base?: string;
}

export const emptyWalk = (): WalkState => ({ at: 0, ed: {}, site: {}, note: {}, noteOpen: null, ok: {}, ans: {}, pb: {}, pbw: {}, wo: {}, extra: "" });

// --- What the owner changes, the same on a computer and a phone ---

/** The owner's words for a section (or the summary); words back as they were are no change. Changing a part unchecks it. */
export const setEdit = (s: WalkState, id: string, value: string, orig: string, partId: string): WalkState => {
  const ed = { ...s.ed };
  if (value === orig) delete ed[id];
  else ed[id] = value;
  const ok = { ...s.ok };
  delete ok[partId];
  return { ...s, ed, ok };
};

export const dropEdit = (s: WalkState, id: string): WalkState => {
  const ed = { ...s.ed };
  delete ed[id];
  return { ...s, ed };
};

/** An answer: an option's index or the owner's own words; null or blank takes it back. */
export const setAnswer = (s: WalkState, k: number, v: number | string | null): WalkState => {
  const ans = { ...s.ans };
  if (v === null || (typeof v === "string" && !v.trim())) delete ans[k];
  else ans[k] = v;
  return { ...s, ans };
};

/**
 * "Not right" on a problem it found, or taking it back. Why: taking a "Not right" back returns the finding to
 * how the profile had it, so it is no change.
 */
export const togglePb = (s: WalkState, k: number): WalkState => {
  const base = s.base ? ((JSON.parse(s.base) as { pb?: Record<number, "noted" | "wrong"> }).pb ?? {}) : {};
  const pb = { ...s.pb };
  const pbw = { ...s.pbw };
  if (pb[k] === "wrong") {
    if (base[k] === "noted") pb[k] = "noted";
    else delete pb[k];
    delete pbw[k];
  } else pb[k] = "wrong";
  return { ...s, pb, pbw };
};

/** "Not right" on a rule for the kind of business: it is taken out, with what is right; again puts it back. */
export const toggleRule = (s: WalkState, k: number): WalkState => {
  const wo = { ...s.wo };
  if (wo[k] !== undefined) delete wo[k];
  else wo[k] = "";
  return { ...s, wo };
};

const answered = (st: WalkState, k: number) => st.ans[k] !== undefined && st.ans[k] !== "";

export const openQs = (x: Part, st: WalkState) => x.qs.filter((k) => !answered(st, k));

export function edited(x: Part, st: WalkState): boolean {
  return (
    x.secs.some((id) => st.ed[id] !== undefined) ||
    (x.kind === "site" && (st.ed.summary !== undefined || Object.keys(st.site).length > 0)) ||
    !!(st.note[x.id] ?? "").trim() ||
    (x.kind === "more" && !!st.extra.trim())
  );
}

export const assumed = (x: Part, p: ProductProfile) => x.secs.some((id) => p.sections.find((s) => s.id === id)?.status === "assumed");

export type PartState = "ok" | "edited" | "you" | "todo";

export function partState(x: Part, st: WalkState, p: ProductProfile): PartState {
  if (st.ok[x.id]) return edited(x, st) ? "edited" : "ok";
  return openQs(x, st).length || assumed(x, p) ? "you" : "todo";
}

export const openAll = (parts: Part[], st: WalkState) =>
  parts.map((x, i) => ({ i, x, n: openQs(x, st).length })).filter((o) => o.n > 0);

export function answerText(p: ProductProfile, st: WalkState, k: number): string {
  const a = st.ans[k];
  if (a === undefined) return "";
  return typeof a === "number" ? (p.questions[k]?.options[a] ?? "") : a;
}

/** Every rule the profile has: the ones in force, then the ones the owner took out. */
export const allRules = (p: ProductProfile) => [...p.watch_outs, ...(p.owner?.rules_off ?? []).map((r) => r.rule)];

/** A finding's first sentence as its headline, the rest as its details. */
export function splitFirst(text: string): [string, string] {
  const m = text.match(/^(.+?[.!?])\s+(\S[\s\S]*)$/);
  return m ? [m[1], m[2]] : [text, ""];
}

interface Said {
  ans: Record<number, number | string>;
  pb: Record<number, "noted" | "wrong">;
  pbw: Record<number, string>;
  wo: Record<number, string>;
  note: Record<string, string>;
  extra: string;
}
const said = (st: WalkState): Said => ({ ans: st.ans, pb: st.pb, pbw: st.pbw, wo: st.wo, note: st.note, extra: st.extra });
const NONE: Said = { ans: {}, pb: {}, pbw: {}, wo: {}, note: {}, extra: "" };

/** How many entries of a record differ from what was saved; blank ones count as none. */
const trimmed = (v: unknown) => (typeof v === "string" ? v.trim() : v) ?? "";
function differ<T>(cur: Record<string | number, T>, old: Record<string | number, T>): number {
  const val = trimmed;
  return [...new Set([...Object.keys(cur), ...Object.keys(old)])].filter((k) => JSON.stringify(val(cur[k])) !== JSON.stringify(val(old[k]))).length;
}

const counts = (st: WalkState) => {
  const b: Said = st.base ? (JSON.parse(st.base) as Said) : NONE;
  const pbKeys = [...new Set([...Object.keys(st.pb), ...Object.keys(st.pbw), ...Object.keys(b.pb), ...Object.keys(b.pbw)])];
  return {
    e: Object.keys(st.ed).length + (Object.keys(st.site).length ? 1 : 0),
    n: differ(st.note, b.note) + (st.extra.trim() !== b.extra.trim() ? 1 : 0),
    a: differ(st.ans, b.ans),
    pb: pbKeys.filter((k) => st.pb[Number(k)] !== b.pb[Number(k)] || (st.pbw[Number(k)] ?? "").trim() !== (b.pbw[Number(k)] ?? "").trim()).length,
    wo: differ(st.wo, b.wo),
  };
};

/**
 * The walk as the owner left it on a saved profile: answers, reviews, rules taken out and notes back where
 * they were given, every part checked. A profile saved before these were kept in place has them in its
 * "Your words" text, which is read back once.
 */
export function walkFrom(p: ProductProfile, parts: Part[]): WalkState {
  const st = emptyWalk();
  const old = oldWords(p);
  p.questions.forEach((q, k) => {
    const a = q.answer ?? old.after("Your answers:", `${q.question} `);
    if (!a) return;
    const j = q.options.indexOf(a);
    st.ans[k] = j >= 0 ? j : a;
  });
  p.problems.forEach((pr, k) => {
    let r = pr.review;
    const line = r ? null : old.after("Problems on your pages:", `${pr.problem} `);
    if (line) r = line.startsWith("Noted") ? { mark: "noted", right: "" } : { mark: "wrong", right: line.replace(/^Not right\.\s*/, "") };
    if (!r) return;
    st.pb[k] = r.mark;
    if (r.right) st.pbw[k] = r.right;
  });
  allRules(p).forEach((rule, k) => {
    const off = p.owner?.rules_off.find((r) => r.rule === rule);
    const why = off ? off.right : old.after("Rules for this kind of business that are not right:", `Not right: ${rule}`);
    if (off || why !== null) st.wo[k] = (why ?? "").trim();
  });
  for (const x of parts) {
    const n = p.owner?.notes[x.id] ?? old.after("What the website does not say:", `${x.title}: `);
    if (n) st.note[x.id] = n;
  }
  st.extra = p.owner?.extra ?? old.extra;
  st.ok = Object.fromEntries(parts.map((x) => [x.id, true]));
  st.base = JSON.stringify(said(st));
  return st;
}

/** The "Your words" text an older save wrote: its lines under each heading, and the free note at the end. */
function oldWords(p: ProductProfile) {
  const text = p.sections.find((s) => s.id === WORDS)?.text ?? "";
  const under = new Map<string, string[]>();
  let head = "";
  const extra: string[] = [];
  for (const line of text.split("\n")) {
    const t = line.trim();
    if (/^[A-Z][^:]{3,80}:$/.test(t) && !t.startsWith("- ")) {
      head = t;
      continue;
    }
    if (head === "Anything else we should know:") extra.push(line);
    else if (t.startsWith("- ")) under.set(head, [...(under.get(head) ?? []), t.slice(2)]);
  }
  return {
    after: (heading: string, prefix: string): string | null => {
      const hit = (under.get(heading) ?? []).find((l) => l.startsWith(prefix));
      return hit === undefined ? null : hit.slice(prefix.length).trim();
    },
    extra: extra.join("\n").trim(),
  };
}

/** Whether anything differs from where the walk started: the save bar's question. */
export function hasChanges(st: WalkState): boolean {
  return Object.values(counts(st)).some((n) => n > 0);
}

/** Whether the owner changed this part since the walk started: its words, its note, its answers or its reviews. */
export function changedPart(x: Part, st: WalkState): boolean {
  const b: Said = st.base ? (JSON.parse(st.base) as Said) : NONE;
  const same = (a: unknown, c: unknown) => JSON.stringify(trimmed(a)) === JSON.stringify(trimmed(c));
  if (x.secs.some((id) => st.ed[id] !== undefined)) return true;
  if (x.kind === "site" && (st.ed.summary !== undefined || Object.keys(st.site).length > 0)) return true;
  if (!same(st.note[x.id], b.note[x.id])) return true;
  if (x.kind === "more" && !same(st.extra, b.extra)) return true;
  if (x.qs.some((k) => !same(st.ans[k], b.ans[k]))) return true;
  return x.kind === "found" && (differ(st.wo, b.wo) > 0 || differ(st.pb, b.pb) > 0 || differ(st.pbw, b.pbw) > 0);
}

/** The prototype's one-line account of what the owner changed. */
export function changesLine(st: WalkState): string {
  const { e, n, a, pb, wo } = counts(st);
  const bits = [
    e && `edited ${e} part${e > 1 ? "s" : ""}`,
    n && `added ${n} note${n > 1 ? "s" : ""}`,
    a && `answered ${a} question${a > 1 ? "s" : ""}`,
    pb && `reviewed ${pb} problem${pb > 1 ? "s" : ""}`,
    wo && `took out ${wo} rule${wo > 1 ? "s" : ""}`,
  ].filter((b): b is string => typeof b === "string");
  if (!bits.length) return "No changes yet: everything stays as it was found.";
  return `You ${bits.length > 1 ? `${bits.slice(0, -1).join(", ")} and ${bits[bits.length - 1]}` : bits[0]}.`;
}

/** The same profile, its keys sorted, to tell a real change from none. */
function canonical(v: unknown): string {
  if (Array.isArray(v)) return `[${v.map(canonical).join(",")}]`;
  if (v && typeof v === "object")
    return `{${Object.keys(v)
      .filter((k) => (v as Record<string, unknown>)[k] !== undefined)
      .toSorted()
      .map((k) => `${JSON.stringify(k)}:${canonical((v as Record<string, unknown>)[k])}`)
      .join(",")}}`;
  return JSON.stringify(v);
}

/**
 * The profile to confirm: the owner's edits in place, and what else they said kept where they said it (each
 * answer on its question, each review on its problem, rules they took out with why, their notes), so a
 * later visit shows it as they left it and every run reads it. Null when nothing changed.
 */
export function correctedProfile(p: ProductProfile, st: WalkState, parts: Part[]): ProductProfile | null {
  const rules = allRules(p);
  const notes = Object.fromEntries(parts.filter((x) => (st.note[x.id] ?? "").trim()).map((x) => [x.id, st.note[x.id].trim()]));
  const rulesOff = rules.flatMap((rule, k) => (st.wo[k] === undefined ? [] : [{ rule, right: st.wo[k].trim() }]));
  const owner = { notes, extra: st.extra.trim(), rules_off: rulesOff };
  const hasOwner = Object.keys(notes).length > 0 || !!owner.extra || rulesOff.length > 0;
  const { owner: _was, ...rest } = p;
  const out: ProductProfile = {
    ...rest,
    business: st.site.name?.trim() || p.business,
    website: st.site.website?.trim() || p.website,
    summary: st.ed.summary ?? p.summary,
    // Why: what an older save put in "Your words" now lives where it was said, read back by walkFrom.
    sections: p.sections.filter((s) => s.id !== WORDS).map((s) => (st.ed[s.id] !== undefined ? { ...s, text: st.ed[s.id] } : s)),
    questions: p.questions.map((q, k) => {
      const { answer: _a, ...q0 } = q;
      const a = answerText(p, st, k).trim();
      return a ? { ...q0, answer: a } : q0;
    }),
    problems: p.problems.map((pr, k) => {
      const { review: _r, ...pr0 } = pr;
      return st.pb[k] ? { ...pr0, review: { mark: st.pb[k], right: (st.pbw[k] ?? "").trim() } } : pr0;
    }),
    // Why: a rule the owner says is wrong leaves the rules every goal reads; it is kept aside with why, so no run brings it back.
    watch_outs: rules.filter((_, k) => st.wo[k] === undefined),
    ...(hasOwner ? { owner } : {}),
  };
  return canonical(out) === canonical(p) ? null : out;
}

const WORDS = "your-words";
